<template>
  <b-card class="shadow-sm p-2 p-md-2" style="border-radius: 12px;">
    <!-- Header Section -->
    <div class="text-center text-md-left">
      <h3 class="font-weight-bold">Income Tax Calculation</h3>
      <p class="mb-0" style="font-size: 0.95rem; line-height: 1.5;">
        Please upload a <strong>PDF document (max 10 pages)</strong> containing your applicable tax rules.  
        Ensure it follows the required guidelines below for accurate AI processing.
      </p>
    </div>

    <!-- Instructions Section (Scrollable on Mobile) -->
    <div class="instructions-scroll-wrapper mt-4">
      <div class="instructions-container bg-light p-3 rounded">
        <h5 class="font-weight-bold mb-2">Instructions</h5>
        <p class="mb-1" style="line-height: 1.5;">
          Please upload a PDF document containing your applicable tax rules. To ensure accurate processing by our AI, follow these requirements:
        </p>
        <ul class="pl-3 mb-1" style="line-height: 1.6;">
          <li><strong>Language:</strong> The document must be written in English.</li>
          <li><strong>Length:</strong> Maximum of 10 pages.</li>
          <li><strong>Content to Include:</strong></li>
          <ul class="pl-3">
            <li>Clear description of tax categories (e.g., VAT, sales tax, service tax).</li>
            <li>Applicable rates or percentages for each category.</li>
            <li>Conditions or thresholds (e.g., exemptions, minimum/maximum amounts, region-specific rules).</li>
            <li>Any formulas or calculation methods if applicable.</li>
            <li>Effective dates of the rules (if time-based).</li>
            <li>References to jurisdictions or regions where rules apply.</li>
          </ul>
        </ul>
        <p class="mb-1" style="line-height: 1.5;">
          Make sure the document is organized, concise, and free of unnecessary information, so AI can read and apply the rules correctly.
        </p>
        <p class="text-danger font-weight-bold mb-0">
          Note: If your tax regulations change, you must upload a new version of the PDF.
        </p>
      </div>
    </div>

    <!-- Form Section -->
    <validation-observer ref="incomeTaxPolicyValidation">
      <b-form v-on:submit.prevent="validationForm" class="mt-4">
        <b-row class="d-flex justify-content-center align-content-center flex-wrap">

          <!-- File upload section -->
          <b-col cols="12" md="6" lg="4" class="mb-3">
            <b-form-group label="" label-for="title" class="required-label mb-2">
              <validation-provider
                #default="{ errors }"
                name="Income Tax Calculation"
                rules="size:2048|ext:jpeg,png,jpg,pdf,doc"
                vid="document"
              >
                <b-form-file
                  id="document"
                  v-model="document"
                  :state="errors.length > 0 ? false : null"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  browse-text="Browse File"
                  class="rounded"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>

          <!-- Submit button -->
          <b-col cols="12" md="3" class="d-flex align-items-start justify-content-center mt-2 mt-md-0">
            <template v-if="isIncomeTaxPolicyFormSubmitLoading">
              <b-button class="float-right" variant="primary" disabled block>
                <b-spinner small class="mr-50" /> Loading...
              </b-button>
            </template>

            <template v-else>
              <b-button
                type="submit"
                block
                class="float-right"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                style="min-width: 120px;"
              >
                Submit
              </b-button>
            </template>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>

    <!-- Divider -->
    <hr class="my-4" />

    <!-- Download section -->
    <div class="text-center mt-3">
      <b-button
        @click="download"
        variant="warning"
        class="d-inline-flex align-items-center px-4 py-2"
        style="border-radius: 8px; font-weight: 500;"
      >
        <feather-icon icon="DownloadIcon" class="mr-50" />
        Download
      </b-button>
    </div>
  </b-card>
</template>

    <script>
    import {
      BCard,
      BAvatar,
      BBadge,
      BPagination,
      BFormGroup,
      BFormInput,
      BFormSelect,
      BDropdown,
      BDropdownItem,
      BButton,
      BForm,
      BModal,
      BSpinner,
      BRow,
      BCol, BFormFile,
    } from 'bootstrap-vue'
import { VueGoodTable } from "vue-good-table";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required, max } from "@validations";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import { salaryTypeConstants } from "@/helpers/constant/salaryTypeConstant";

import {
  INCOME_TAX_POLICY_CREATE,
  INCOME_TAX_POLICY_EDIT,
  INCOME_TAX_POLICY_DELETE,
  INCOME_TAX_POLICY_SHOW,
} from "@/helpers/permissionsConstant";
import FormatCurrency from "@/layouts/components/FormatCurrency.vue";
import { INCOME_TAX_INFINITE_AMOUNT } from "@/helpers/constant/incomeTaxInfiniteValueConstant"


export default {
  name: "IncomeTaxPolicyView",
  components: {
    BFormFile,
    FormatCurrency,
    BForm,
    BButton,
    BCard,
    VueGoodTable,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    ValidationProvider,
    ValidationObserver,
    BModal,
    BSpinner,
    BRow,
    BCol,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      INCOME_TAX_POLICY_CREATE,
      INCOME_TAX_POLICY_EDIT,
      INCOME_TAX_POLICY_DELETE,
      INCOME_TAX_POLICY_SHOW,

      INCOME_TAX_INFINITE_AMOUNT,

      document: null,
      isIncomeTaxPolicyFormSubmitLoading: false,
    };
  },
  async created() {
    try {
      // get document here
    } catch (error) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Error",
          icon: "BellIcon",
          variant: "danger",
          text: error?.response?.data?.message,
        },
      });
    }
  },

  methods: {
    resetModal() {
      this.document = null;
    },

    validationForm: async function () {
      this.$refs.incomeTaxPolicyValidation.validate().then(async (success) => {
        if (success) {
          try {
            this.isIncomeTaxPolicyFormSubmitLoading = true;
            const formData = new FormData();

            if (this.document) {
              formData.append("document", this.document);
            }

            await this.$api.post(`/api/income-tax-policies`, formData, {
              headers: {
                "Content-Type": "multipart/form-data",
              }
            });

            this.isIncomeTaxPolicyFormSubmitLoading = false;
            this.resetModal();

            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Tax calculation logic uploaded successfully.",
              },
            });
          } catch (error) {
            this.isIncomeTaxPolicyFormSubmitLoading = false;
            if (error?.response?.data?.message) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: "Error",
                  icon: "BellIcon",
                  variant: "danger",
                  text: error?.response?.data?.message,
                },
              });
            }

            if (error?.response?.data?.errors) {
              this.$refs.incomeTaxPolicyValidation.setErrors(
                error?.response?.data?.errors
              );
            }
          }
        }
      });
    },

    async download() {
      try {
        const response = await this.$api.get(`/api/income-tax-policies/download`, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');

// Assuming the file content type is returned by OpenAI API
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'income-tax';

        if (contentDisposition) {
          const matches = contentDisposition.match(/filename=["']?([^"']+)["']?/);
          if (matches && matches[1]) {
            fileName = matches[1]; // Extracted filename
          }
        }

// Create an object URL for the Blob
        link.href = URL.createObjectURL(blob);

// Set the download attribute with the extracted filename
        link.setAttribute('download', fileName);

// Append the link to the document, trigger the download, and remove it
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

      } catch (error) {
        console.error('Download failed', error);
      }
    }
  },
};
</script>

    <style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.required-label label::after {
  content: " *";
  color: red;
}
</style>

<style scoped>
.instructions-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.instructions-container {
  min-width: 500px; /* ensures horizontal scroll on mobile */
  border-left: 4px solid #007bff;
}

/* Make the scroll area obvious */
.instructions-scroll-wrapper::-webkit-scrollbar {
  height: 6px;
}
.instructions-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(0, 123, 255, 0.4);
  border-radius: 4px;
}
.instructions-scroll-wrapper::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Better reading experience on small devices */
@media (max-width: 768px) {
  .instructions-container {
    padding: 1rem;
    font-size: 0.9rem;
  }
  h5 {
    font-size: 1.1rem;
  }
}
</style>